import { useState } from 'react'
import { cleanPhoneInput, formatPhone, phoneProblem, type FieldDef } from '@sonder/shared'
import { useClientView } from '@/app/mode'
import type { SectionEditor } from '@/data/editor'
import { reviewInfo, useNameOf } from '@/data/review'
import { Field, Input, OpenLink, Textarea } from '@/ui'

const INPUT_TYPE: Partial<Record<FieldDef['kind'], string>> = { url: 'url', email: 'email', text: 'text' }

/** Binds one registry field to one dotted path in a section editor. */
/** `labelHidden` and `hideOptional` are for places where context already says what the box is and that it's optional. */
export function FieldEditor({ editor, path, def, label, hint, rows, labelHidden, hideOptional, action }: { editor: SectionEditor<unknown>; path: string; def: FieldDef; label?: string; hint?: string; rows?: number; labelHidden?: boolean; hideOptional?: boolean; action?: React.ReactNode }) {
  const clientView = useClientView()
  const f = editor.getField<string>(path)
  const nameOf = useNameOf()
  const review = clientView ? reviewInfo(f, nameOf, { confirmable: def.confirmable ?? true, onConfirm: (v) => editor.confirm(path, v), onUndo: () => editor.undo(path) }) : undefined
  const common = { value: f?.value ?? '', placeholder: def.placeholder, autoComplete: 'off' as const }
  // Phone problems show once the person leaves the box, not while they are mid-number.
  const [typing, setTyping] = useState(false)
  const error = def.kind === 'phone' && !typing ? (phoneProblem(common.value) ?? undefined) : undefined
  return (
    <Field
      label={label ?? def.label}
      hint={hint ?? def.hint}
      error={error}
      labelHidden={labelHidden}
      optional={!def.required && !hideOptional}
      action={action}
      review={review}
      anchor={path}
    >
      {def.kind === 'textarea' ? (
        <Textarea {...common} rows={rows ?? 3} autoGrow onChange={(e) => editor.setValue(path, e.target.value)} />
      ) : def.kind === 'url' ? (
        <div className="flex items-center gap-2">
          <Input {...common} type="url" inputMode="url" onChange={(e) => editor.setValue(path, e.target.value)} />
          <OpenLink value={common.value} />
        </div>
      ) : def.kind === 'phone' ? (
        <Input
          {...common}
          type="tel"
          inputMode="tel"
          onFocus={() => setTyping(true)}
          onChange={(e) => editor.setValue(path, cleanPhoneInput(e.target.value))}
          onBlur={() => {
            setTyping(false)
            const formatted = formatPhone(common.value)
            if (formatted !== common.value) editor.setValue(path, formatted)
          }}
        />
      ) : def.kind === 'text' ? (
        // Wraps rather than hiding the end of a long answer ("Restaurant / bar · Oaxacan and northern Mexican…").
        <Textarea {...common} singleLine onChange={(e) => editor.setValue(path, e.target.value)} />
      ) : (
        <Input {...common} type={INPUT_TYPE[def.kind] ?? 'text'} onChange={(e) => editor.setValue(path, e.target.value)} />
      )}
    </Field>
  )
}
